import type { Capability } from './capabilities';

/**
 * The product surfaces, and the capability each one needs.
 *
 * A "feature" here is a thing a person would name if you asked what they use
 * the portal for - Assignments, Report Studio, the Map. It is the unit the nav
 * shows, the unit a route subtree is gated on, and the unit an administrator
 * provisions. Sub-pages are not features; they are capabilities within one.
 *
 * The shell renders whatever is in this list that the user holds. That is the
 * whole rule, and it is why there is no longer a branch in the shell naming
 * any particular product: a user provisioned only for Ratings sees only
 * Ratings because Ratings is the only entry they hold, not because the shell
 * checks for it.
 */

export const FEATURE_IDS = [
  'assignments',
  'crm',
  'reportStudio',
  'map',
  'agents',
  'vault',
  'connectors',
  'ratings',
  'faq'
] as const;

export type FeatureId = (typeof FEATURE_IDS)[number];

export interface FeatureDefinition {
  id: FeatureId;
  /** What it is called on screen. The nav item's title comes from here. */
  title: string;
  /** Held to see the feature at all. `null` for the ones everyone gets. */
  capability: Capability | null;
}

export const FEATURES: Record<FeatureId, FeatureDefinition> = {
  assignments: {
    id: 'assignments',
    title: 'Assignments',
    capability: 'assignments.view'
  },
  crm: { id: 'crm', title: 'CRM', capability: 'crm.view' },
  reportStudio: {
    id: 'reportStudio',
    title: 'Report Studio',
    capability: 'reportStudio.view'
  },
  map: { id: 'map', title: 'Map', capability: 'map.view' },
  agents: { id: 'agents', title: 'Agents', capability: 'agents.view' },
  vault: { id: 'vault', title: 'Data Vault', capability: 'vault.view' },
  connectors: { id: 'connectors', title: 'Connectors', capability: 'connectors.view' },
  ratings: { id: 'ratings', title: 'Ratings', capability: 'ratings.view' },
  /** Help is not provisioned; anyone signed in can read it. */
  faq: { id: 'faq', title: 'FAQ', capability: null }
};
